import React, { useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { MultiScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { Button } from '../../../components/Button/Button'
import { SelectDropdown } from '../../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import './Settings.scss'
import { AuditLogConfig } from '../../../models/Account'

const RETENTION_UNIT_OPTIONS = [
  { id: 'days', label: 'days' },
  { id: 'months', label: 'months' },
]

const DEFAULT_SEARCHABLE_RETENTION_DAYS = 7
const DEFAULT_ARCHIVE_RETENTION_DAYS = 90
const DAYS_PER_MONTH = 30
const MAX_DAYS_FOR_MONTH_DISPLAY = 360

type RetentionUnit = 'days' | 'months'

interface RetentionState {
  value: number | ''
  unit: RetentionUnit
}

const convertDaysToDisplay = (days: number | null): RetentionState => {
  if (days === null || days === 0) return { value: '', unit: 'days' }
  if (days % DAYS_PER_MONTH === 0 && days <= MAX_DAYS_FOR_MONTH_DISPLAY) {
    return { value: days / DAYS_PER_MONTH, unit: 'months' }
  }
  return { value: days, unit: 'days' }
}

const convertToStorageDays = (retention: RetentionState): number | null => {
  if (!retention.value) return null
  return retention.unit === 'months' ? Number(retention.value) * DAYS_PER_MONTH : Number(retention.value)
}

const isArchiveRetentionEnabled = (retention: RetentionState): boolean => {
  return retention.value !== '' && retention.value !== 0
}

export const AuditLogSettings: React.FC = () => {
  useDocumentTitle('Audit Log Settings')
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isUpdating }] = useUpdateAccountMutation()
  const { hasQuota } = useZocksQuota()
  const auditLogsEnabledSettingAvailable = hasQuota('auth.auditLogEnabled.value')

  const effectiveConfig = account?.data?.effectiveConfig?.auditLogConfig
  const auditLogEnabled = effectiveConfig?.enabled ?? false

  const [userInputSearchableRetention, setUserInputSearchableRetention] = useState<RetentionState | null>(null)
  const [userInputArchiveRetention, setUserInputArchiveRetention] = useState<RetentionState | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)

  const searchableRetention =
    userInputSearchableRetention ?? convertDaysToDisplay(effectiveConfig?.searchableRetentionDays ?? DEFAULT_SEARCHABLE_RETENTION_DAYS)

  const archiveRetention = userInputArchiveRetention ?? convertDaysToDisplay(effectiveConfig?.archiveRetentionDays ?? null)

  const isToggleDisabled = isAccountFetching || !auditLogsEnabledSettingAvailable
  const isFormDisabled = !auditLogsEnabledSettingAvailable || isAccountFetching

  const handleToggleAuditLogs = async (checked: boolean) => {
    if (!account) {
      return
    }

    const nextConfig = {
      ...account.data.config,
      auditLogConfig: {
        enabled: checked,
        searchableRetentionDays: convertToStorageDays(searchableRetention),
        archiveRetentionDays: checked ? convertToStorageDays(archiveRetention) : null,
      } as AuditLogConfig,
    }

    setUserInputSearchableRetention(null)
    setUserInputArchiveRetention(null)

    await updateAccount({
      accountId,
      payload: {
        config: nextConfig,
      },
    })
  }

  const handleSave = async () => {
    if (!account) {
      return
    }

    const searchableDays = convertToStorageDays(searchableRetention)
    const archiveDays = convertToStorageDays(archiveRetention)

    if (archiveDays !== null && searchableDays !== null && archiveDays <= searchableDays) {
      setValidationError('Archive retention period cannot be lower or equal than searchable retention period.')
      return
    }

    setValidationError(null)

    const nextConfig = {
      ...account.data.config,
      auditLogConfig: {
        enabled: auditLogEnabled,
        searchableRetentionDays: auditLogEnabled ? searchableDays : null,
        archiveRetentionDays: auditLogEnabled ? archiveDays : null,
      } as AuditLogConfig,
    }

    await updateAccount({
      accountId,
      payload: {
        config: nextConfig,
      },
    })
  }

  const getMaxValue = (unit: RetentionUnit): number => {
    return unit === 'months' ? 12 : 365
  }

  const handleRetentionChange = (value: string, unit: RetentionUnit, setter: React.Dispatch<React.SetStateAction<RetentionState | null>>) => {
    const numValue = value === '' ? '' : Number(value)
    const maxValue = getMaxValue(unit)

    if (numValue === '' || (numValue >= 1 && numValue <= maxValue)) {
      setter({ value: numValue, unit })
    }
  }

  return (
    <Container className='d-flex flex-column align-items-start zocks-gap-1'>
      <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded w-100 m-0'>
        <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
          <div className='d-flex align-self-center gap-3 align-items-center'>
            <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
            <span className='zocks-text-gray-900'>Audit logs track account activity for compliance and security. Set default retention periods below.</span>
          </div>
        </Col>
      </Row>
      <Row className='gap-3 p-0 align-items-center w-100 m-0 mb-4'>
        <Col xs='auto' className='p-0'>
          <div className='paragraph-medium'>Enable audit logs</div>
        </Col>
        <Col className='p-0 d-flex justify-content-end'>
          <MultiScopedBlock
            scopedBlocks={[
              {
                scope: { scope: 'account:compliance_config_update', superScope: [] },
                missingScopeAction: MissingScopeAction.DISABLE,
              },
              {
                scope: { scope: 'account:update', superScope: ['account:admin'] },
                missingScopeAction: MissingScopeAction.DISABLE,
              },
            ]}
          >
            <Form.Switch disabled={isToggleDisabled} checked={auditLogEnabled} onChange={(e) => handleToggleAuditLogs(e.currentTarget.checked)} />
          </MultiScopedBlock>
        </Col>
      </Row>

      {auditLogEnabled && (
        <>
          <Row className='zocks-mb p-0 align-items-center w-100 m-0 border-bottom zocks-border-color-gray-200'>
            <Col xs={4} className='p-2'>
              <div className='lead-small'>Type</div>
            </Col>
            <Col xs={4} className='p-2 text-center'>
              <div className='lead-small'>Status</div>
            </Col>
            <Col xs={4} className='p-2 text-end'>
              <div className='lead-small'>Retention</div>
            </Col>
          </Row>

          <Row className='zocks-mb p-0 align-items-center w-100 m-0 border-bottom zocks-border-color-gray-200'>
            <Col xs={4} className='p-2'>
              <div className='paragraph-medium'>Searchable logs</div>
            </Col>
            <Col xs={4} className='p-2 text-center'>
              <i className='bi bi-check2 fs-5 zocks-text-primary' />
            </Col>
            <Col xs={4} className='p-2'>
              <div className='d-flex gap-2'>
                <Form.Control
                  type='number'
                  min={1}
                  max={getMaxValue(searchableRetention.unit)}
                  className='w-auto zocks-border-color-gray-400'
                  value={searchableRetention.value}
                  onChange={(e) => {
                    handleRetentionChange(e.target.value, searchableRetention.unit, setUserInputSearchableRetention)
                    setValidationError(null)
                  }}
                  disabled={isFormDisabled}
                />
                <SelectDropdown
                  options={RETENTION_UNIT_OPTIONS}
                  className='zocks-px-0.75'
                  initialSelectedOption={RETENTION_UNIT_OPTIONS.find((option) => option.id === searchableRetention.unit)}
                  onOptionSelected={(option) => {
                    const newUnit = option.id as RetentionUnit
                    const defaultValue = newUnit === 'days' ? DEFAULT_SEARCHABLE_RETENTION_DAYS : 1
                    setUserInputSearchableRetention({ value: defaultValue, unit: newUnit })
                    setValidationError(null)
                  }}
                  disabled={isFormDisabled}
                />
              </div>
            </Col>
          </Row>
          <Row className='gap-0 p-0 align-items-center w-100 m-0'>
            <Col xs={4} className='p-2'>
              <div className='paragraph-medium'>Archived logs</div>
            </Col>
            <Col xs={4} className='p-2 text-center'>
              <Form.Check
                type='checkbox'
                checked={isArchiveRetentionEnabled(archiveRetention)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setUserInputArchiveRetention({ value: DEFAULT_ARCHIVE_RETENTION_DAYS, unit: 'days' })
                  } else {
                    setUserInputArchiveRetention({ value: '', unit: 'days' })
                  }
                  setValidationError(null)
                }}
                disabled={isFormDisabled}
              />
            </Col>
            <Col xs={4} className='p-2'>
              <div className='d-flex gap-2' style={{ minHeight: '40px' }}>
                {isArchiveRetentionEnabled(archiveRetention) ? (
                  <>
                    <Form.Control
                      type='number'
                      min={1}
                      max={getMaxValue(archiveRetention.unit)}
                      className='w-auto zocks-border-color-gray-400'
                      value={archiveRetention.value}
                      onChange={(e) => {
                        handleRetentionChange(e.target.value, archiveRetention.unit, setUserInputArchiveRetention)
                        setValidationError(null)
                      }}
                      disabled={isFormDisabled}
                    />
                    <SelectDropdown
                      options={RETENTION_UNIT_OPTIONS}
                      className='zocks-px-0.75 py-2'
                      initialSelectedOption={RETENTION_UNIT_OPTIONS.find((option) => option.id === archiveRetention.unit)}
                      onOptionSelected={(option) => {
                        const newUnit = option.id as RetentionUnit
                        const defaultValue = newUnit === 'days' ? DEFAULT_ARCHIVE_RETENTION_DAYS : 3
                        setUserInputArchiveRetention({ value: defaultValue, unit: newUnit })
                        setValidationError(null)
                      }}
                      disabled={isFormDisabled}
                    />
                  </>
                ) : null}
              </div>
            </Col>
          </Row>
          {validationError && (
            <Row className='w-100 m-0'>
              <Col className='p-0'>
                <FormFeedback className='d-block'>{validationError}</FormFeedback>
              </Col>
            </Row>
          )}
          <Row className='w-100 m-0'>
            <Col className='p-0'>
              <MultiScopedBlock
                scopedBlocks={[
                  {
                    scope: { scope: 'account:compliance_config_update', superScope: [] },
                    missingScopeAction: MissingScopeAction.HIDE,
                  },
                  {
                    scope: { scope: 'account:update', superScope: ['account:admin'] },
                    missingScopeAction: MissingScopeAction.HIDE,
                  },
                ]}
              >
                <Button variant='primary' onClick={handleSave} disabled={isFormDisabled || isUpdating}>
                  Save
                </Button>
              </MultiScopedBlock>
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
